import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { fundPickerLabel, useRangeFunds, type RangeWeightsTable } from "./rangeFunds";

interface ReAddHoldingDialogProps {
  weightsTable: RangeWeightsTable;
  /** Portfolio numbers in this range, with the labels used across its UI (P1…P5, R1…R5, MM…). */
  portfolios: { num: number; label: string }[];
  /** Name of the range as it appears in this page's copy — "P", "R", "Dist", "MM". */
  rangeLabel: string;
  invalidateKeys: string[][];
}

/**
 * Re-add a previously held fund to a range.
 *
 * The counterpart to the Remove button. Removal is a SOFT delete — it writes `weight_pct = 0` at
 * the exit date and keeps every historical row — but the management tables only list funds whose
 * latest weight is above zero, so an exited fund disappeared from the page with no way back. The
 * "Add Fund" dialog was not that way back: it INSERTS a new ba_cbam_funds row, which collides with
 * the `ba_cbam_funds_fe_fund_name_key` UNIQUE (fe_fund_name) constraint and surfaces a raw
 * Postgres 23505 error.
 *
 * This writes a new non-zero weight row against the EXISTING fund row instead, which is all a
 * re-entry is. `ba_cbam_funds.is_current` is deliberately not touched: `trg_sync_is_current`
 * recomputes it from the weight tables on write, so it flips back to true on its own — and stays
 * false, correctly, if the re-entry is back-dated behind the exit. See rangeFunds.ts.
 */
export default function ReAddHoldingDialog({
  weightsTable,
  portfolios,
  rangeLabel,
  invalidateKeys,
}: ReAddHoldingDialogProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [fundId, setFundId] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split("T")[0]);
  const [weights, setWeights] = useState<Record<number, number>>(
    Object.fromEntries(portfolios.map((p) => [p.num, 0])),
  );

  const { previouslyHeld } = useRangeFunds(weightsTable);

  const reset = () => {
    setFundId("");
    setEffectiveDate(new Date().toISOString().split("T")[0]);
    setWeights(Object.fromEntries(portfolios.map((p) => [p.num, 0])));
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!fundId) throw new Error("Select a fund to re-add");
      const rows = portfolios
        .filter((p) => Number(weights[p.num] || 0) > 0)
        .map((p) => ({
          fund_id: fundId,
          portfolio: p.num,
          weight_pct: Number(weights[p.num]),
          effective_date: effectiveDate,
        }));
      if (!rows.length) throw new Error("Enter a weight above 0% for at least one portfolio");
      const { error } = await supabase
        .from(weightsTable)
        .upsert(rows, { onConflict: "fund_id,portfolio,effective_date" });
      if (error) throw error;
    },
    onSuccess: () => {
      for (const k of invalidateKeys) queryClient.invalidateQueries({ queryKey: k });
      queryClient.invalidateQueries({ queryKey: ["ba-cbam-funds-active"] });
      toast.success(`Holding re-added to ${rangeLabel}`);
      setOpen(false);
      reset();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const selected = previouslyHeld.find((f) => f.id === fundId);

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <RotateCcw className="h-4 w-4 mr-1" /> Re-add Holding
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Re-add a previously held fund to {rangeLabel}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Funds that were sold out of {rangeLabel}. Re-adding writes a new weight row against the
            existing fund record from the date you choose — the fund's history and sector mappings
            are untouched, and nothing is duplicated.
          </p>

          <div>
            <Label>Fund</Label>
            <Select value={fundId} onValueChange={setFundId}>
              <SelectTrigger><SelectValue placeholder="Select a previously held fund..." /></SelectTrigger>
              <SelectContent>
                {previouslyHeld.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{fundPickerLabel(f)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {previouslyHeld.length === 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                No previously held funds in {rangeLabel}.
              </p>
            )}
          </div>

          <div>
            <Label>Effective Date</Label>
            <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
            <p className="mt-1 text-[11px] text-muted-foreground">
              The date the position re-opens. Back-dating is honoured: the fund counts as held from
              this date onward, and reconstructions before it are unchanged.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Weights</Label>
            {portfolios.map((p) => (
              <div key={p.num} className="flex items-center gap-3">
                <span className="text-sm font-medium w-16">{p.label}</span>
                <Input
                  type="number"
                  step="0.01"
                  className="w-24 h-8 text-xs"
                  value={weights[p.num]}
                  onChange={(e) => setWeights({ ...weights, [p.num]: parseFloat(e.target.value) || 0 })}
                />
                <span className="text-xs text-muted-foreground">%</span>
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">
              Portfolios left at 0% get no row, so the fund stays out of them.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={!selected || save.isPending}>
              {save.isPending ? "Saving…" : "Re-add Holding"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
